import React, { useEffect, useState } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ExternalSearchProfileInfo } from './ExternalSearchProfileInfo';
import { ExternalSearchModalTabs } from './ExternalSearchModalTabs';
import { ExternalSearchService } from '../../service';
import { IDetailModalData } from '../../dto/IDetailModalData';
import { IAudienceCharts } from '../../../offer/dto/IAudienceCharts';
import {
    copyToClipboard,
    getCorrectSocialUrl,
    getFilteredChartsData,
} from '../../../../../../../shared/functions/Functions';
import { TextField } from '@mui/material';
import { InternalRoutes } from '../../../../../../../enum/InternalRoutes';
import { FaCopy } from 'react-icons/fa';
import { ErrorToast, SuccessToast } from '../../../../../../../utils/toasters';
import { ConfirmationModal } from '../../../../../../../shared/components/modal/ConfirmationModal';
import { actions } from '../../../../../../../store/authentication/authRedux';
import { useDispatch } from 'react-redux';
import ExternalProfileSavedListsCard from './ExternalProfileSavedListsCard';
import ExternalProfileCollaborationsLeftCard from './ExternalProfileCollaborationsLeftCard';
import { MODAL_LIMITED_KEY, NO_PROFILE_TOKENS_KEY } from '../../../../../../../utils/constants';
import {
    closeSubscriptionModal,
    openRefillModal,
    openUpgradeModal,
} from '../../../../../../../store/subscription/subscriptionSlice';

interface IExternalSearchDetailModalProps {
    show: boolean;
    closeModal: () => void;
    id: number;
    isInternal?: boolean;
    updateHistory?: React.Dispatch<React.SetStateAction<boolean>>;
    fromExternalSearch?: boolean;
    isGlobalDiscovery?: boolean;
    updateAlreadyViewedAye?: () => void;
}

export function ExternalSearchDetailModal({
    show,
    closeModal,
    id,
    isInternal,
    updateHistory,
    fromExternalSearch,
    isGlobalDiscovery = false,
    updateAlreadyViewedAye,
}: IExternalSearchDetailModalProps) {
    const { t } = useTranslation();
    const [profileData, setProfileData] = useState<IDetailModalData>();
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [allCharts, setAllCharts] = useState<IAudienceCharts>({
        engagementChart: [],
        followersChart: [],
        followingChart: [],
        likesChart: [],
    });

    const [activeTab, setActiveTab] = useState('audience');
    const campaignId = sessionStorage.getItem('activeCampaignId');
    const dispatch = useDispatch();
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (show) {
            ExternalSearchService.getHistoryProfileDetail(id, isInternal, fromExternalSearch)
                .then(response => {
                    setProfileData(response);
                    dispatch(actions.updateDiscoveryDetails(response?.discoveryDetails));
                    setAllCharts(getFilteredChartsData(response.audience.chartData));
                    if (updateAlreadyViewedAye) {
                        updateAlreadyViewedAye();
                    }
                })
                .catch(err => {
                    setHasError(true);
                    if (err?.response?.data?.message === NO_PROFILE_TOKENS_KEY) {
                        closeModal();
                        dispatch(
                            openRefillModal({
                                source: NO_PROFILE_TOKENS_KEY,
                            })
                        );
                    } else if (err?.response?.data?.message === MODAL_LIMITED_KEY) {
                        closeModal();
                        dispatch(
                            openUpgradeModal({
                                source: NO_PROFILE_TOKENS_KEY,
                            })
                        );
                    } else {
                        ErrorToast(err);
                    }
                });
        }
    }, [id]);

    function closeAllModals() {
        dispatch(closeSubscriptionModal());
        setShowConfirmModal(false);
        setHasError(false);
        setProfileData(undefined);
        closeModal();
    }

    function sendRequest() {
        ExternalSearchService.sendRequest(id, campaignId)
            .then(() => {
                SuccessToast(t('brand.campaign.book.external_search.successfully_requested'));
                setShowConfirmModal(false);
                if (updateHistory) {
                    updateHistory(prevState => !prevState);
                }
                closeAllModals();
            })
            .catch(err => ErrorToast(err));
    }

    return (
        <>
            <ConfirmationModal
                title={t('brand.campaign.book.external_search.confirm_title')}
                description={t('brand.campaign.book.external_search.confirm_desc')}
                show={showConfirmModal}
                closeModal={() => closeAllModals()}
                action={sendRequest}
            />
            {!hasError && profileData && (
                <Modal
                    show={show}
                    onHide={closeAllModals}
                    size="xl"
                    id="offer-modal"
                    aria-labelledby="contained-modal-title-vcenter"
                    centered
                >
                    <Modal.Body>
                        <Row className="h-100">
                            <Col xl={3} lg={12} className="profile-info px-0 px-md-3 d-none d-xl-block">
                                <ExternalSearchProfileInfo
                                    handle={profileData?.externalUser?.title ?? ''}
                                    href={`${getCorrectSocialUrl(
                                        profileData?.audience?.platformDataArray?.[0].mainPlatform
                                            ?.platformEnum as string,
                                        profileData?.audience?.platformDataArray?.[0]?.statistic?.title ?? '',
                                        profileData.audience?.platformDataArray?.[0]?.statistic?.externalLink ?? ''
                                    )}`}
                                    photoUrl={profileData?.externalUser?.photoUrl ?? ''}
                                    email={profileData?.externalUser?.email ?? ''}
                                    socialIcon={profileData?.externalUser?.mainPlatform?.path}
                                />
                                {!!profileData?.discoveryLists?.length ? (
                                    <ExternalProfileSavedListsCard discoveryLists={profileData?.discoveryLists} />
                                ) : null}
                                {profileData?.collaboration?.brandCollaborations?.length > 0 && (
                                    <ExternalProfileCollaborationsLeftCard
                                        brands={profileData?.collaboration?.brandCollaborations}
                                        setActiveTab={setActiveTab}
                                    />
                                )}
                            </Col>
                            <Col xl={9} lg={12} style={{ height: 'calc(100% - 64px)' }}>
                                <ExternalSearchModalTabs
                                    about={profileData?.externalUser?.about}
                                    audienceData={profileData?.audience}
                                    charts={allCharts}
                                    feed={profileData?.externalUser?.feed}
                                    modashData={profileData?.externalImageData}
                                    collaboration={profileData.collaboration}
                                    activeTab={activeTab}
                                    setActiveTab={setActiveTab}
                                />
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="justify-content-between">
                        {profileData?.externalUser?.isExternal ? (
                            <div className="w-md-50">
                                {isGlobalDiscovery ? null : (
                                    <div className="d-flex align-items-center">
                                        <TextField
                                            value={InternalRoutes.REGISTER_LINK}
                                            name="handle"
                                            label={t('brand.campaign.book.external_search.register_link')}
                                            variant="outlined"
                                            size="small"
                                            className="w-100 ml-md-3"
                                        />
                                        <div
                                            style={{ marginLeft: '-1.5rem', zIndex: 1 }}
                                            className="cursor-pointer"
                                            onClick={() => copyToClipboard(InternalRoutes.REGISTER_LINK)}
                                        >
                                            <FaCopy className="text-muted" size={20} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div></div>
                        )}
                        <div>
                            {profileData?.externalUser?.isExternal && !isGlobalDiscovery ? (
                                <Button variant="primary" size="sm" onClick={() => setShowConfirmModal(true)}>
                                    {t('general.button.request')}
                                </Button>
                            ) : null}

                            <Button className="ml-2" variant="outline-primary" size="sm" onClick={closeModal}>
                                {t('general.button.cancel')}
                            </Button>
                        </div>
                    </Modal.Footer>
                </Modal>
            )}
        </>
    );
}
